import { IncomingMessage, ServerResponse } from 'http'
import { NextPageContext } from 'next'
import * as cookie from 'cookie'
import { getPlatformHostFromRequest } from '@reward-platform/platform-config/server'
import { getSession } from '@reward-platform/auth0-b2c'
import { isClient } from '~/utils/envChecks'

// Re-exported so callers (e.g. ~/pages/_app.tsx) can pull both the server-only
// helpers below and the shared, client-safe auth-path matching from one place.
export { buildUnrestrictedRootPaths, isAuthRequiredForPath } from '~/utils/isAuthRequiredForPath'

interface ServerNextContext extends NextPageContext {
  req: IncomingMessage
  res: ServerResponse
}

export const isServerPageContext = (context: NextPageContext): context is ServerNextContext =>
  'req' in context && 'res' in context && !isClient()

export const getCookiesForClientOrServer = (context: NextPageContext) => {
  if (!isServerPageContext(context)) {
    // client-side: Get cookies from document.cookie
    return cookie.parse(document.cookie)
  }
  // server-side: Get cookies from the request headers
  return cookie.parse(context.req?.headers.cookie ?? '')
}

// For current whitelabel set up
export const getHostForClientOrServer = (
  context: NextPageContext,
  checkForValidForwardedHost?: boolean
) => {
  if (!isServerPageContext(context)) {
    // client-side: Get host from document.location
    return window?.location?.host ?? ''
  }
  // server-side: Get host from the request headers
  if (checkForValidForwardedHost) {
    // Validate and use forwarded host if avios
    return getPlatformHostFromRequest(context.req)
  }

  return context.req.headers.host ?? ''
}

export const isUserAuthenticated = async (
  req: IncomingMessage,
  res: ServerResponse
): Promise<boolean> => {
  try {
    const session = await getSession(req, res)
    return !!session?.user
  } catch {
    return false
  }
}
